/* ============================================================
   teg — system.

   The detail behind the overview: every core, the memory split,
   temperatures with their own limits, and what the machine is
   made of. Specs come from /api/hardware, which is cached for an
   hour because none of it changes without a reboot.
   ============================================================ */

import React, { useEffect, useState } from 'react';
import { Icon, Meter, Spark, fmtBytes, fmtRate, fmtUptime, level, tempLevel } from './lib';
import { api } from './api';

export default function System({ token, stats, history }) {
  const [hw, setHw] = useState(null);
  const [hwError, setHwError] = useState('');
  const [gpu, setGpu] = useState(null);
  const [checks, setChecks] = useState(null);
  const [showPorts, setShowPorts] = useState(false);

  useEffect(() => {
    let live = true;
    api.hardware(token)
      .then((d) => { if (live) setHw(d); })
      .catch((e) => { if (live) setHwError(e.message); });
    const pull = () => {
      api.gpu(token).then((d) => { if (live) setGpu(d); }).catch(() => {});
      api.checks(token).then((d) => { if (live) setChecks(d); }).catch(() => {});
    };
    pull();
    const id = setInterval(() => { if (!document.hidden) pull(); }, 15000);
    return () => { live = false; clearInterval(id); };
  }, [token]);

  const cpu = stats.cpu || {};
  const cores = cpu.percent || [];
  const mem = stats.memory || {};
  const swap = stats.swap || {};
  const temps = stats.temps || [];

  return (
    <div className="tg-view">
      <div className="tg-grid tg-grid--two">
        <div className="panel">
          <div className="panel-head">
            <span className="label">Processor</span>
            <span className="meta">{Math.round(cpu.avg || 0)}% average</span>
          </div>
          <div className="tg-cores">
            {cores.map((v, i) => (
              <div key={i} className={`tg-core tg-hint ${level(v) ? `is-${level(v)}` : ''}`}
                   data-hint={`core ${i} · ${v.toFixed(0)}%`} tabIndex={0}>
                <span className="tg-core-bar"><span className="tg-core-fill" style={{ height: `${Math.max(2, v)}%` }}/></span>
                <span className="tg-core-n">{i}</span>
              </div>
            ))}
            {cores.length === 0 && <span className="meta">no per-core data</span>}
          </div>
          <dl className="tg-facts">
            <div><dt>Model</dt><dd title={hw?.cpu?.model}>{hw?.cpu?.model || '—'}</dd></div>
            <div><dt>Cores / threads</dt><dd>{hw ? `${hw.cpu.cores} / ${hw.cpu.threads}` : '—'}</dd></div>
            <div><dt>Clock</dt><dd>{hw?.cpu?.mhz_current ? `${(hw.cpu.mhz_current / 1000).toFixed(2)} GHz` : '—'}</dd></div>
            <div><dt>Load 1 / 5 / 15</dt><dd>{(cpu.load || []).map((l) => l.toFixed(2)).join(' / ') || '—'}</dd></div>
          </dl>
        </div>

        <div className="panel">
          <div className="panel-head"><span className="label">Memory</span>
            <span className="meta">{fmtBytes(mem.used)} of {fmtBytes(mem.total)}</span>
          </div>
          <div className="tg-membody">
            <div className="tg-big">{Math.round(mem.percent || 0)}%</div>
            <Meter pct={mem.percent} height={8}
                   hint={`${fmtBytes(mem.used)} used · ${fmtBytes(mem.available)} available of ${fmtBytes(mem.total)}`}/>
            <dl className="tg-facts">
              <div><dt>Used</dt><dd>{fmtBytes(mem.used)}</dd></div>
              <div><dt>Available</dt><dd>{fmtBytes(mem.available)}</dd></div>
              <div><dt>Total</dt><dd>{fmtBytes(mem.total)}</dd></div>
            </dl>
            <div className="tg-swap">
              <span className="label">Swap</span>
              <Meter pct={swap.total ? (swap.used / swap.total) * 100 : 0} warn={40} err={70} height={5}
                     hint={swap.total ? `${fmtBytes(swap.used)} of ${fmtBytes(swap.total)}` : 'no swap configured'}/>
              <span className="meta">{swap.total ? `${fmtBytes(swap.used)} of ${fmtBytes(swap.total)}` : 'not configured'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="tg-grid tg-grid--two">
        <div className="panel tg-chart">
          <div className="panel-head"><span className="label">CPU and memory</span>
            <span className="meta">cpu {Math.round(cpu.avg || 0)}% · memory {Math.round(mem.percent || 0)}%</span>
          </div>
          <Spark a={history.cpu} b={history.ram} height={64} label="cpu and memory"
                 labels={['cpu', 'memory']} format={(v) => `${v.toFixed(0)}%`}/>
          <div className="tg-legend">
            <span className="tg-key tg-key--a">cpu</span><span className="tg-key tg-key--b">memory</span>
          </div>
        </div>
        <div className="panel tg-chart">
          <div className="panel-head"><span className="label">Network</span>
            <span className="meta">↓ {fmtRate(stats.network?.recv_per_s)} · ↑ {fmtRate(stats.network?.sent_per_s)}</span>
          </div>
          <Spark a={history.netIn} b={history.netOut} height={64} label="network throughput"
                 labels={['in', 'out']} format={(v) => fmtRate(v)}/>
          <div className="tg-legend">
            <span className="tg-key tg-key--a">in</span><span className="tg-key tg-key--b">out</span>
          </div>
        </div>
        <div className="panel tg-chart">
          <div className="panel-head"><span className="label">Disk I/O</span>
            <span className="meta">read {fmtRate(stats.disk_io?.read_per_s)} · write {fmtRate(stats.disk_io?.write_per_s)}</span>
          </div>
          <Spark a={history.dRead} b={history.dWrite} height={64} label="disk throughput"
                 labels={['read', 'write']} format={(v) => fmtRate(v)}/>
          <div className="tg-legend">
            <span className="tg-key tg-key--a">read</span><span className="tg-key tg-key--b">write</span>
          </div>
        </div>
      </div>

      {temps.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="label">Temperatures</span></div>
          <div className="tg-temp-rows">
            {temps.map((t, i) => (
              <div key={`${t.label}-${i}`} className="tg-temp-row">
                <span className="tg-temp-name">{t.label}</span>
                <Meter pct={t.critical ? (t.current / t.critical) * 100 : (t.current / 100) * 100} warn={75} err={90} height={5}
                       hint={`${t.label}: ${Math.round(t.current)}°C${t.critical ? ` · limit ${Math.round(t.critical)}°C` : ''}`}/>
                <span className={`tg-temp-v ${tempLevel(t.current) ? `is-${tempLevel(t.current)}` : ''}`}>{Math.round(t.current)}°C</span>
                <span className="meta tg-temp-lim">{t.critical ? `limit ${Math.round(t.critical)}°` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="tg-grid tg-grid--two">
        <div className="panel">
          <div className="panel-head"><span className="label">Graphics</span>
            {gpu && <span className="meta">{gpu.adapters.length} adapter{gpu.adapters.length === 1 ? '' : 's'}</span>}
          </div>
          {!gpu && <div className="skeleton tg-skel"/>}
          {gpu && (
            <div className="tg-gpu">
              {gpu.adapters.map((a) => {
                const card = gpu.cards.find((c) => c.driver && a.driver && c.driver === a.driver);
                // An NVIDIA card reports through nvidia-smi, not sysfs: it has
                // no gt_cur_freq_mhz, which is an i915 file. Matching on the
                // bound driver is what stops the Intel chip's numbers being
                // drawn under the NVIDIA card's name.
                const nv = a.driver === 'nvidia'
                  ? (gpu.nvidia || []).find((n) => !a.model || n.name.includes('GeForce') || n.name.includes('NVIDIA'))
                  : null;
                return (
                  <div key={a.slot} className={`tg-gpu-card ${a.in_use ? '' : 'is-idle'}`}>
                    <div className="tg-gpu-top">
                      <Icon name="gpu" size={15}/>
                      <span className="tg-gpu-name">{a.model}</span>
                      <span className="meta">{a.driver || 'no driver'}</span>
                    </div>

                    {nv ? (
                      <>
                        <div className="tg-gpu-freq">
                          <span className="tg-big">{nv.utilization ?? 0}</span><span className="meta">% used</span>
                          {nv.temperature != null && (
                            <span className={`tg-temp-v ${tempLevel(nv.temperature) ? `is-${tempLevel(nv.temperature)}` : ''}`}>
                              {Math.round(nv.temperature)}°C
                            </span>
                          )}
                        </div>
                        <Meter pct={nv.utilization ?? 0} warn={80} err={95} height={5}
                               hint={`${nv.utilization ?? 0}% of the GPU`}/>
                        {nv.memory_total_mb ? (
                          <>
                            <Meter pct={(nv.memory_used_mb / nv.memory_total_mb) * 100} warn={85} err={95} height={5}
                                   hint={`${nv.memory_used_mb} MB of ${nv.memory_total_mb} MB VRAM`}/>
                            <span className="meta">
                              {nv.memory_used_mb} MB of {nv.memory_total_mb} MB VRAM
                              {nv.clock_mhz != null && ` · ${nv.clock_mhz} MHz core`}
                              {nv.fan_percent != null && ` · fan ${nv.fan_percent}%`}
                            </span>
                          </>
                        ) : null}
                        <span className="meta">
                          driver {nv.driver_version}
                          {/* This card reports a limit but no live draw, which
                              is a fact about the card rather than a gap. */}
                          {nv.power_w != null
                            ? ` · ${Math.round(nv.power_w)} W of ${Math.round(nv.power_limit_w ?? 0)} W`
                            : nv.power_limit_w != null
                              ? ` · ${Math.round(nv.power_limit_w)} W limit (no live draw reported)`
                              : ''}
                        </span>
                      </>
                    ) : card?.freq_mhz != null ? (
                      <>
                        <div className="tg-gpu-freq">
                          <span className="tg-big">{card.freq_mhz}</span><span className="meta"> MHz</span>
                        </div>
                        <Meter pct={card.freq_max_mhz ? (card.freq_mhz / card.freq_max_mhz) * 100 : 0} warn={80} err={95} height={5}
                               hint={`${card.freq_mhz} MHz of ${card.freq_max_mhz ?? '?'} MHz`}/>
                        <span className="meta">idles at {card.freq_min_mhz ?? '?'} · tops out at {card.freq_max_mhz ?? '?'} MHz</span>
                      </>
                    ) : (
                      <p className="meta tg-note">
                        {a.in_use
                          ? 'This driver publishes no clocks or utilisation.'
                          : 'No driver is bound, so the card is idle and reports nothing.'}
                      </p>
                    )}
                  </div>
                );
              })}
              {gpu.temps.map((t) => (
                <div key={t.label} className="tg-gpu-temp">
                  <span className="meta">{t.label}</span>
                  <span className={`tg-temp-v ${tempLevel(t.current) ? `is-${tempLevel(t.current)}` : ''}`}>{Math.round(t.current)}°C</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="label">Listening on</span>
            {checks && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowPorts((v) => !v)}>
                {showPorts ? 'Show public only' : `All ${checks.listeners.length}`}
              </button>
            )}
          </div>
          {!checks && <div className="skeleton tg-skel"/>}
          {checks && (
            <div className="tg-ports">
              {checks.listeners
                .filter((l) => showPorts || l.public)
                .map((l) => (
                  <div key={`${l.ip}:${l.port}`} className="tg-port">
                    <span className="tnum tg-port-n">{l.port}</span>
                    <span className="tg-port-proc">{l.process || '—'}</span>
                    <span className="meta">{l.public ? 'all interfaces' : l.ip}</span>
                  </div>
                ))}
              {checks.listeners.filter((l) => showPorts || l.public).length === 0 && (
                <div className="meta tg-note">Nothing listening beyond localhost.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="label">This machine</span>
          {hw && <span className="meta">booted {new Date(hw.boot_time * 1000).toLocaleString()}</span>}
        </div>
        {hwError && <div className="alert alert--err tg-banner">{hwError}</div>}
        {!hw && !hwError && <div className="skeleton tg-skel"/>}
        {hw && (
          <div className="tg-specs">
            <dl className="tg-facts">
              <div><dt>Host</dt><dd>{hw.hostname}</dd></div>
              <div><dt>OS</dt><dd title={hw.os}>{hw.os}</dd></div>
              <div><dt>Kernel</dt><dd>{hw.kernel}</dd></div>
              <div><dt>Architecture</dt><dd>{hw.arch}</dd></div>
              <div><dt>Memory</dt><dd>{fmtBytes(hw.memory_total)}</dd></div>
              <div><dt>Uptime</dt><dd>{fmtUptime(stats.uptime)}</dd></div>
            </dl>

            <div className="tg-spec-block">
              <span className="label">Graphics</span>
              {hw.gpus.map((g) => (
                <div key={g.slot} className="listrow tg-spec-row">
                  <Icon name="gpu" size={15}/>
                  <span className="tg-spec-name">{g.model}</span>
                  <span className="meta">{g.driver || 'no driver'}</span>
                </div>
              ))}
              {hw.gpus.some((g) => g.driver === 'nouveau') && (
                <p className="meta tg-note">
                  Utilisation and memory need NVIDIA's own driver; with nouveau the only reading the
                  card gives up is its temperature, which is in the list above.
                </p>
              )}
            </div>

            <div className="tg-spec-block">
              <span className="label">Disks</span>
              {hw.disks.map((d) => (
                <div key={d.name} className="listrow tg-spec-row">
                  <Icon name="disk" size={15}/>
                  <span className="tg-spec-name">/dev/{d.name} {d.model && <span className="meta">{d.model}</span>}</span>
                  <span className="meta">{fmtBytes(d.size)} · {d.kind}{d.bus ? ` · ${d.bus}` : ''}</span>
                </div>
              ))}
            </div>

            <div className="tg-spec-block">
              <span className="label">Network</span>
              {hw.nics.map((n) => (
                <div key={n.name} className="listrow tg-spec-row">
                  <Icon name="net" size={15}/>
                  <span className="tg-spec-name">{n.name} <span className="meta">{n.ip || 'no address'}</span></span>
                  <span className="meta">{n.up ? (n.speed_mbps ? `${n.speed_mbps} Mb/s` : 'up') : 'down'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
